import { useCallback, useMemo } from 'react';

import useCompanies from 'contexts/basicData/useCompanies';

export interface ResolvedCoordinates {
  latitude: number;
  longitude: number;
}

/**
 * Slår upp den sparade GPS-geotaggen för det aktiva företag som en adress pekar på,
 * eller undefined när adressen inte är ett geotaggat företag (fritextadress eller
 * ogeotaggat företag).
 */
export type CompanyCoordinateResolver = (
  name: string | undefined,
  address: string | undefined,
  zip: string | undefined,
  city: string | undefined
) => ResolvedCoordinates | undefined;

// Samma nyckel som backends AssignmentCoordinateResolver steg 1:
// string.Join(" ", Name, Address, Zip, City), exakt matchning. Medvetet lika strikt —
// en lösare matchning riskerar att släppa laddbufferten på ett annat företags geotagg.
const coordinateKey = (
  name: string | undefined,
  address: string | undefined,
  zip: string | undefined,
  city: string | undefined
) => [name ?? '', address ?? '', zip ?? '', city ?? ''].join(' ');

/**
 * Klientspegling av steg 1 i backends <c>AssignmentCoordinateResolver</c>: företagens
 * sparade geotaggar. Låter formuläret räkna laddbuffertens 10 km-regel LIVE, innan
 * uppdraget sparats och geokodats server-side (utan geotagg fanns koordinater bara på
 * redan sparade uppdrag, så förhandsvisningen föll alltid tillbaka på länsregeln).
 *
 * Resolvern täcker inte resolverns steg 2–3 (LiveETALog, HERE-geokodning) — de kräver
 * serveranrop. Fritextadresser och ogeotaggade företag ger därför ingen koordinat, och
 * förhandsvisningen visar länsregeln tills servern räknat om vid sparning.
 */
const useCompanyCoordinateResolver = (): CompanyCoordinateResolver => {
  const { companies } = useCompanies();

  const coordinatesByKey = useMemo(() => {
    const result = new Map<string, ResolvedCoordinates>();
    companies.forEach((company) => {
      // useCompanies filtrerar redan bort inaktiva, men backend gör det explicit — behåll
      // villkoret så att spegeln inte tystnar om lookupen någon gång byter källa.
      if (
        !company.active ||
        company.latitude == null ||
        company.longitude == null
      ) {
        return;
      }
      result.set(
        coordinateKey(company.name, company.address, company.zip, company.city),
        { latitude: company.latitude, longitude: company.longitude }
      );
    });
    return result;
  }, [companies]);

  return useCallback(
    (name, address, zip, city) =>
      coordinatesByKey.get(coordinateKey(name, address, zip, city)),
    [coordinatesByKey]
  );
};

export default useCompanyCoordinateResolver;
